import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { InfoBox } from '../../../info-box/info-box';
import {
  DonglesIntegrationItem,
  DroneHarmonyIntegrationItem,
  IntegrationsContext,
  LeicaIntegrationItem,
  XsiteIntegrationItem,
} from '../../state/integrations-state';
import { getDeleteIntegrationDialogService } from './delete-integration-dialog-factory';

enum DeleteIntegrationStatus {
  Idle,
  Pending,
  Error,
}

interface Props {
  item:
    | XsiteIntegrationItem
    | LeicaIntegrationItem
    | DonglesIntegrationItem
    | DroneHarmonyIntegrationItem;
  onClose: () => void;
}

const DeleteIntegrationDialog = (props: Props) => {
  const { t } = useTranslation('integrations');
  const { removeIntegration } = React.useContext(IntegrationsContext);
  const [status, setStatus] = React.useState(DeleteIntegrationStatus.Idle);
  const deleteIntegrationService = getDeleteIntegrationDialogService(props.item);

  const cancelCloseButtonClick = () => {
    props.onClose();
  };

  const deleteIntegration = async () => {
    try {
      setStatus(DeleteIntegrationStatus.Pending);

      await deleteIntegrationService?.deleteIntegration();

      removeIntegration(props.item);
      cancelCloseButtonClick();
    } catch {
      setStatus(DeleteIntegrationStatus.Error);
    }
  };

  return (
    <Dialog
      closeIcon={status !== DeleteIntegrationStatus.Pending}
      closeOnDimmerClick={true}
      useGoogleTranslate={true}
      width={500}
      onClose={cancelCloseButtonClick}
    >
      {{
        header: t('deleteDialog.header', { ns: 'integrations' }),
        content: (
          <Content>
            <Text>
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="deleteDialog.confirmQuestion"
                ns="integrations"
                values={{ name: props.item.name, provider: props.item.provider }}
              />
            </Text>

            {status === DeleteIntegrationStatus.Error ? (
              <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
                {t('deleteDialog.deleteError', { ns: 'integrations' })}
                <br />
                <br />
                {t('deleteDialog.contactSupport', { ns: 'integrations' })}
              </InfoBox>
            ) : status === DeleteIntegrationStatus.Pending ? (
              <InfoBox color="yellow" leftIcon={{ icon: 'spinner', spin: true }} topMargin={true}>
                {t('oneMoment', { ns: 'common' })}
              </InfoBox>
            ) : (
              <>
                <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
                  {t('deleteDialog.fileSyncWarning', { ns: 'integrations' })}
                </InfoBox>
                {props.item.key === 'dongles' && (
                  <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
                    {t('deleteDialog.donglesWarning', { ns: 'integrations' })}
                  </InfoBox>
                )}
              </>
            )}
          </Content>
        ),
        footer: {
          right: (
            <>
              <Button
                color="error"
                loading={status === DeleteIntegrationStatus.Pending}
                variant="contained"
                onClick={deleteIntegration}
              >
                {t('deleteDialog.deleteButton', { ns: 'integrations' })}
              </Button>
              <Button
                disabled={status === DeleteIntegrationStatus.Pending}
                variant="text"
                onClick={cancelCloseButtonClick}
              >
                {t('deleteDialog.closeButton', { ns: 'integrations' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Text = styled.span<{ topMargin?: boolean; bottomMargin?: boolean }>`
  display: block;
  line-height: 1.3em;

  margin-top: ${(props) => (props.topMargin ? '0.5em' : undefined)};
  margin-bottom: ${(props) => (props.bottomMargin ? '0.5em' : undefined)};

  strong {
    font-weight: 500;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

export { DeleteIntegrationDialog };
